import Container from '@/components/Container';

import DefaultContentTemplateTextEditor from '@/components/DefaultContentTemplate/TextEditor';
import Button from '@/components/Button';
import LessonCard from '@/components/Learn/Lesson';
import DefaultContentTemplateAuthorDetailed from '@/components/DefaultContentTemplate/AuthorDetailed';
import CheckIcon from '@/components/Icons/CheckIcon';

import { fetchCourseBySlug, fetchRelatedContent, getPage, normalizeLearnItems } from '@/services/learn/course.service';
import { useEffect, useState } from 'react';
import { CourseEntity, CoursePage as CoursePageEntity, LearnEntity } from '@/graphql/generated/graphql';
import { RelatedContent } from '@/components/DefaultContentTemplate/RelatedContent';
import { useNavigate, useParams } from 'react-router-dom';
import CourseSkeleton from './Course.skeleton';
import { PlayIcon } from '@heroicons/react/24/solid';
import { fetchCourseLearn } from '@/services/learn/learn.service';
import { getAuthors, getStartLesson } from '@/utils/learn';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import Navigation, { NavigationSkeleton } from '@/components/ui/navigation';
import { navigationCourse } from '../constants';
import Breadcrumb from '@/components/ui/breadcrumb';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';

import Highlight from './highlight';
import Divider from '@/components/Divider';
import { trackCourseOpen } from '@/lib/ga-custom-events/course';

function CoursePage() {
  const [cx, setCourse] = useState<CourseEntity | null>(null);
  const [learn, setLearn] = useState<LearnEntity | null>(null);
  const [relatedContent, setRelatedContent] = useState<Array<RelatedContent> | null>(null);
  const [page, setPage] = useState<CoursePageEntity | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const { slug } = useParams();

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);

      const [courseResult, relatedContentResult, learnResult, pageReult] = await Promise.all([
        fetchCourseBySlug(slug),
        fetchRelatedContent(slug),
        fetchCourseLearn(slug),
        getPage(),
      ]);

      setCourse(courseResult);
      trackCourseOpen(courseResult!);
      setLearn(learnResult);
      setPage(pageReult.page);
      setRelatedContent(relatedContentResult);
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <>
        <NavigationSkeleton />
        <CourseSkeleton />
      </>
    );
  }

  const c = cx?.attributes;
  const normalizedLearnItems = normalizeLearnItems(learn);

  if (c === null) {
    return null;
  }

  const authors = getAuthors(c);
  const startLesson = getStartLesson(c!, learn);
  let startLessonlabel = 'BEGIN';

  if (learn && learn?.attributes?.completed) {
    startLessonlabel = 'WATCH AGAIN';
  }

  if (learn && !learn?.attributes?.completed) {
    startLessonlabel = 'CONTINUE';
  }

  const items = [
    {
      label: <>Learn</>,
      url: '/learn',
    },
    {
      label: <>Courses</>,
      url: '/learn',
    },
    { label: c?.title },
  ];

  return (
    <>
      <Navigation title="Learn" items={navigationCourse} />
      <AnimatedFadeInComponent>
        <Breadcrumb items={items} />
      </AnimatedFadeInComponent>
      <Container className="default-content-template min-h-40 !gap-0">
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          {/* <div className="mb-sh-30">
            <PageHeader title="Learn" url="/learn" type="none" />
          </div> */}
          {learn?.attributes?.completed && c?.courseCompletedSection?.title && c?.courseCompletedSection?.message ? (
            <AnimatedFadeInComponent>
              <div className="mb-sh-30 rounded-2xl border border-sh-blue-light p-5">
                <h4 className="flex items-center text-sh-md font-bold">
                  <CheckIcon className="mr-2 inline-block h-auto w-4 transition duration-300 ease-in" fill="#1775E3" />
                  {c?.courseCompletedSection?.title}
                </h4>
                <p className="text-sh-sm">{c?.courseCompletedSection?.message}</p>
              </div>
            </AnimatedFadeInComponent>
          ) : null}
        </div>
        <div className="col-span-12">
          <AnimatedFadeInComponent>
            <Highlight
              cx={cx}
              video={c?.hero?.data?.attributes?.mime?.includes('video') ? c?.hero?.data?.attributes?.url : ''}
              image={c?.hero?.data?.attributes?.mime?.includes('image') ? c?.hero?.data?.attributes?.url : ''}
              title={c?.title}
              description={c?.description}
              button={
                <>
                  {startLesson ? (
                    <div className="col-span-12 ">
                      <AnimatedFadeInComponent>
                        <Button
                          theme="solid"
                          onClick={() =>
                            navigate(
                              `/learn/course/${c?.slug}/module/${startLesson?.idModule}/lesson/${startLesson?.idLesson}`,
                            )
                          }
                          className="col-span-12 !mt-0">
                          {startLessonlabel === 'WATCH AGAIN' ? (
                            <ArrowPathIcon className="inline-block h-4 w-4" />
                          ) : (
                            <PlayIcon className="inline-block h-4 w-4" />
                          )}
                          <span>{startLessonlabel} COURSE</span>
                        </Button>
                      </AnimatedFadeInComponent>
                    </div>
                  ) : null}
                </>
              }
            />
          </AnimatedFadeInComponent>
        </div>

        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <Divider />
          <div className="flex flex-col gap-sh-15">
            {c?.textContent?.markdownText ? (
              <div className="mb-sh-30">
                <AnimatedFadeInComponent>
                  <DefaultContentTemplateTextEditor
                    component={{
                      id: 'textContent',
                      markdownText: c?.textContent?.markdownText || '',
                      markdownHtml: c?.textContent?.markdownText || '',
                    }}
                    className="!my-0"
                  />
                </AnimatedFadeInComponent>
              </div>
            ) : null}

            <div className="col-span-12 lg:col-start-3 lg:col-end-11">
              {c?.modules?.map((m, idx) => (
                <AnimatedFadeInComponent key={`${m?.title}-${idx}`}>
                  <div key={`${m?.title}-${idx}`}>
                    <p className="mb-sh-10 font-larken text-sh-20-28">{m?.title}</p>
                    <p className="text-sh-18-32 mb-6 mt-0 font-aeonik">{m?.description}</p>
                    <div className="flex flex-col gap-sh-30">
                      {m?.lessons?.map((lesson) => {
                        const doneLearnItem = !!normalizedLearnItems?.find(
                          (learnItem) => learnItem?.idModule === m?.id && learnItem?.idLesson === lesson?.id,
                        );
                        return (
                          <LessonCard
                            title={lesson?.title || ''}
                            description={lesson?.description || ''}
                            thumbnail={lesson!.thumbnail}
                            key={lesson?.id}
                            slug={lesson?.id}
                            done={doneLearnItem}
                            watchLabel={page?.lesson?.beginButtonLabel || undefined}
                            watchAgainLabel={page?.lesson?.watchAgainButtonlabel || undefined}
                            lockedLabel={page?.lesson?.lockedButtonlabel || undefined}
                            tag="Lesson"
                            duration={lesson?.duration || ''}
                            url={`/learn/course/${c?.slug}/module/${m.id}/lesson/${lesson?.id}`}
                          />
                        );
                      })}
                    </div>
                    {idx === (c?.modules?.length || 0) - 1 ? null : <Divider />}
                  </div>
                </AnimatedFadeInComponent>
              ))}

              {authors ? (
                <AnimatedFadeInComponent>
                  <>
                    <Divider isBody />
                    <DefaultContentTemplateAuthorDetailed
                      component={{
                        id: 'DefaultContentTemplateAuthorDetailed',
                        authors: {
                          data: authors,
                        },
                      }}
                    />
                  </>
                </AnimatedFadeInComponent>
              ) : null}
            </div>
          </div>

          {(relatedContent || []).length > 0 ? (
            <></>
          ) : //   <div className="">
          //     <h3 className="mb-6 text-sh-md font-bold">You might enjoy</h3>
          //     <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          //       {relatedContent!.map((course) => {
          //         return <Card {...course} key={course.title} />;
          //       })}
          //     </div>
          //   </div>
          null}
        </div>
      </Container>
    </>
  );
}

export default CoursePage;
